import { Button, Input, Modal, Select } from '@/components/ui';
import { sysadminService, type AsesoriaSysadminDTO } from '@/services/sysadmin.service';
import { getAsesoriaEstadoLabel, type AsesoriaEstado } from '@/utils/asesoriaStatus';
import { useState } from 'react';

interface EditarAsesoriaModalProps {
  asesoria: AsesoriaSysadminDTO | null;
  onClose: () => void;
  onSaved: () => void;
}

// The backend auditoría endpoint (UpdateAsesoriaAuditoriaSchema) only accepts these
// three estados. REQUIERE_CORRECCIONES / REVERTIDA are managed by other flows, so we
// never send them here — otherwise the PATCH is rejected with a 400.
const SUPPORTED_ESTADOS: AsesoriaEstado[] = ['EN_CURSO', 'EN_REVISION', 'FINALIZADA'];

function isSupportedEstado(estado: string): boolean {
  return (SUPPORTED_ESTADOS as string[]).includes(estado);
}

// Options the user can pick. If the asesoría's current estado is not one the backend
// accepts, show it (marked "actual") so the select reflects reality and the value isn't
// silently degraded — but the user can only move it to a supported estado.
function buildEstadoOptions(current: string): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = SUPPORTED_ESTADOS.map((estado) => ({
    value: estado,
    label: getAsesoriaEstadoLabel(estado),
  }));
  if (!isSupportedEstado(current)) {
    options.unshift({ value: current, label: `${getAsesoriaEstadoLabel(current as AsesoriaEstado)} (actual)` });
  }
  return options;
}

export function EditarAsesoriaModal({ asesoria, onClose, onSaved }: EditarAsesoriaModalProps) {
  const [saving, setSaving] = useState(false);
  const [creandoAudio, setCreandoAudio] = useState(false);

  // Audios de PRUEBA: los audios reales entran por un webhook desde un Google Form, imposible de
  // disparar en desarrollo. Esto inserta un audio dummy asociado a la asesoría para poder probar
  // el flujo de corrección de audios (rechazo/reemplazo/vincular).
  const handleCargarAudioPrueba = async () => {
    if (!asesoria) return;
    setCreandoAudio(true);
    try {
      await sysadminService.crearAudioPrueba(asesoria.asesoriaId);
      onSaved();
    } catch (err) {
      console.error('Error al cargar audio de prueba', err);
      alert('Error al cargar audio de prueba');
    } finally {
      setCreandoAudio(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!asesoria) return;
    setSaving(true);
    try {
      const formData = new FormData(e.currentTarget);
      const estadoSel = formData.get('estado') as string;
      const data: Record<string, unknown> = {
        objetivo: formData.get('objetivo'),
        horas: parseFloat(formData.get('horas') as string),
        recomendaciones: formData.get('recomendaciones'),
        fechaInicio: new Date(formData.get('fechaInicio') as string).toISOString(),
        fechaFin: formData.get('fechaFin') ? new Date(formData.get('fechaFin') as string).toISOString() : null,
      };
      // Only send estado when it's one the backend accepts. Leaving an unsupported
      // estado (REQUIERE_CORRECCIONES / REVERTIDA) untouched simply omits the field.
      if (isSupportedEstado(estadoSel)) {
        data.estado = estadoSel;
      }
      await sysadminService.actualizarAsesoriaAuditoria(asesoria.asesoriaId, data);
      onSaved();
      onClose();
    } catch (err) {
      console.error('Error al actualizar asesoría', err);
      alert('Error al actualizar asesoría');
    } finally {
      setSaving(false);
    }
  };

  const estadoNoEditable = asesoria != null && !isSupportedEstado(asesoria.estado);

  return (
    <Modal isOpen={asesoria != null} onClose={onClose} title={asesoria ? `Editar Asesoría #${asesoria.asesoriaId}` : ''}>
      {asesoria && (
        <form onSubmit={handleSubmit} className="space-y-5 p-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-(--admin-text3) uppercase tracking-wider">Fecha de Inicio</label>
              <Input type="date" name="fechaInicio" defaultValue={asesoria.fechaInicio.split('T')[0]} required />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-(--admin-text3) uppercase tracking-wider">Fecha de Finalización</label>
              <Input type="date" name="fechaFin" defaultValue={asesoria.fechaFin?.split('T')[0]} />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-(--admin-text3) uppercase tracking-wider">Objetivo de la Asesoría</label>
            <Input name="objetivo" defaultValue={asesoria.objetivo} required placeholder="Describa el objetivo principal..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-(--admin-text3) uppercase tracking-wider">Horas Realizadas</label>
              <Input type="number" step="any" min="0" name="horas" defaultValue={asesoria.horas} required />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-(--admin-text3) uppercase tracking-wider">Estado Actual</label>
              <Select name="estado" defaultValue={asesoria.estado} options={buildEstadoOptions(asesoria.estado)} />
              {estadoNoEditable && (
                <p className="text-[11px] text-(--admin-text3)">
                  El estado actual solo puede cambiarse a En Curso, En revisión o Finalizada desde aquí.
                </p>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-(--admin-text3) uppercase tracking-wider">Recomendaciones y Observaciones</label>
            <textarea
              name="recomendaciones"
              className="w-full border border-(--admin-border) focus:ring-2 focus:ring-(--admin-blue) focus:border-transparent outline-none rounded-lg p-3 text-sm min-h-32 bg-(--admin-surface) transition-all"
              defaultValue={asesoria.recomendaciones ?? ''}
              placeholder="Escriba las recomendaciones para la empresa..."
            />
          </div>
          <div className="flex gap-3 justify-between items-center pt-5">
            <Button
              variant="secondary"
              type="button"
              onClick={handleCargarAudioPrueba}
              isLoading={creandoAudio}
              title="Inserta un audio dummy para probar el flujo de corrección (solo dev)"
            >
              + Audio de prueba
            </Button>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={onClose} type="button">
                Cancelar
              </Button>
              <Button type="submit" isLoading={saving} className="min-w-32">
                Guardar Cambios
              </Button>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
}
